import { useState, useMemo } from "react";
import { useRoute } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { X, Search } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { GrokLogo } from "@/components/grok-logo";

interface Document {
  id: string;
  filename: string;
  content: string;
  document_type: string;
  tags: string[];
}

export default function DocumentViewerPage() {
  const [, params] = useRoute("/documents/:id");
  const [, setLocation] = useLocation();
  const documentId = params?.id;
  const [searchQuery, setSearchQuery] = useState("");

  const { data: document, isLoading, error } = useQuery<Document>({
    queryKey: [`/api/documents/${documentId}`],
    enabled: !!documentId,
    retry: false,
    onError: (err: any) => {
      console.error('[Document Viewer] Error fetching document:', err);
    },
  });

  const handleClose = () => {
    // Fallback to library page if no history
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation('/library');
    }
  };

  // Parse JSONL content if the document is a JSONL file
  const parsedData = useMemo(() => {
    if (!document?.content) return null;
    
    const filename = document.filename.toLowerCase();
    if (!filename.endsWith('.jsonl')) return null;
    
    try {
      const lines = document.content.split('\n').filter(line => line.trim().length > 0);
      const parsedObjects: any[] = [];
      
      lines.forEach((line) => {
        try {
          const parsed = JSON.parse(line.trim());
          parsedObjects.push(parsed);
        } catch (error) {
          // Skip invalid JSON lines
        }
      });
      
      if (parsedObjects.length > 0 && typeof parsedObjects[0] === 'object' && parsedObjects[0] !== null) {
        return parsedObjects;
      }
    } catch (error) {
      // If parsing fails, return null to show raw content
    }
    
    return null;
  }, [document]);

  // Get all unique keys from parsed objects for table columns
  const tableColumns = useMemo(() => {
    if (!parsedData || parsedData.length === 0) return [];
    return Array.from(new Set(parsedData.flatMap(obj => Object.keys(obj))));
  }, [parsedData]);

  // Filter parsed data based on search query
  const filteredParsedData = useMemo(() => {
    if (!parsedData || !searchQuery.trim()) return parsedData;
    
    const query = searchQuery.toLowerCase();
    return parsedData.filter((item) => {
      return Object.values(item).some((value) => {
        const stringValue = typeof value === 'object' 
          ? JSON.stringify(value).toLowerCase()
          : String(value || '').toLowerCase();
        return stringValue.includes(query);
      });
    });
  }, [parsedData, searchQuery]);

  // Filter raw content based on search query
  const filteredContent = useMemo(() => {
    if (!document?.content || !searchQuery.trim()) return document?.content || '';
    
    const query = searchQuery.toLowerCase();
    const lines = document.content.split('\n');
    return lines.filter(line => line.toLowerCase().includes(query)).join('\n');
  }, [document, searchQuery]);

  // Highlight search matches in text
  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = text.split(regex);
    
    // Split already creates array with matches and non-matches alternating
    // Check if index is odd (matches) - no need to test regex again
    return parts.map((part, index) => {
      // Odd indices are matches, even indices are non-matches
      if (index % 2 === 1) {
        return (
          <mark 
            key={index} 
            className="bg-[#f5f5f5] text-[#1a1a1a]"
          >
            {part}
          </mark>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  if (!documentId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" style={{ background: '#ffffff' }}>
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-2 text-[#1a1a1a]">Document Not Found</h1>
          <p className="text-[#9b9b9b]">No document ID provided</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" style={{ background: '#ffffff' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a1a] mx-auto mb-4"></div>
          <p className="text-[#9b9b9b]">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    const is404 = errorMessage.includes('404') || errorMessage.includes('not found');
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" style={{ background: '#ffffff' }}>
        <div className="text-center max-w-md px-4">
          <h1 className="text-2xl font-semibold mb-2 text-[#1a1a1a]">Document Not Found</h1>
          <p className="mb-4 text-[#9b9b9b]">
            {is404 
              ? "The document you're looking for doesn't exist or has been removed."
              : `Error loading document: ${errorMessage}`
            }
          </p>
          {documentId && (
            <p className="text-xs mb-4 font-mono text-[#666666]">
              Document ID: {documentId}
            </p>
          )}
          <Button onClick={handleClose} className="mt-4 border border-[#e0e0e0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a]">Go Back</Button>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" style={{ background: '#ffffff' }}>
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-2 text-[#1a1a1a]">Document Not Found</h1>
          <p className="mb-4 text-[#9b9b9b]">
            The document you're looking for doesn't exist or has been removed.
          </p>
          {documentId && (
            <p className="text-xs mb-4 font-mono text-[#666666]">
              Document ID: {documentId}
            </p>
          )}
          <Button onClick={handleClose} className="mt-4 border border-[#e0e0e0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a]">Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-white" 
      style={{ 
        background: '#ffffff',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)'
      }}
    >
      {/* Header */}
      <header 
        className="sticky top-0 z-50 border-b border-[#e0e0e0] bg-white"
        style={{ 
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)'
        }}
      >
        <div className="container mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4" style={{ textAlign: 'left' }}>
            <GrokLogo size={32} className="flex-shrink-0" />
            <h1 className="text-lg font-semibold truncate text-[#1a1a1a]" style={{ textAlign: 'left' }}>
              {document.filename}
            </h1>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-[#e0e0e0] text-[#666666]">
                Type: {document.document_type || 'guideline'}
              </Badge>
              {document.tags && document.tags.length > 0 && (
                <Badge variant="outline" className="border-[#e0e0e0] text-[#666666]">
                  Tags: {document.tags.join(', ')}
                </Badge>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-[#666666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5] touch-manipulation"
            style={{ 
              minHeight: '44px',
              minWidth: '44px',
              fontSize: '16px',
              textAlign: 'center'
            }}
          >
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 py-6 max-w-7xl">
        <Card className="border border-[#e0e0e0] bg-white">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-[#1a1a1a]">
                  {parsedData ? 'Structured Data' : 'Document Content'}
                </CardTitle>
                {parsedData && (
                  <p className="text-sm mt-2 text-[#9b9b9b]">
                    {searchQuery.trim() 
                      ? `Showing ${filteredParsedData.length} of ${parsedData.length} lines`
                      : `Total Lines: ${parsedData.length}`
                    }
                  </p>
                )}
                {!parsedData && searchQuery.trim() && (
                  <p className="text-sm mt-2 text-[#9b9b9b]">
                    {filteredContent ? filteredContent.split('\n').length : 0} matching line(s)
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#666666]" />
                  <Input
                    type="text"
                    placeholder="Search document..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search document content"
                    className="pl-10 w-full sm:w-64 bg-[#f5f5f5] border border-[#e0e0e0] text-[#1a1a1a] placeholder:text-[#b0b0b0] focus:border-[#d0d0d0] focus:ring-1 focus:ring-[#d4d4d4] focus:outline-none"
                    style={{ 
                      fontSize: '16px',
                      textAlign: 'left',
                      WebkitAppearance: 'none',
                      appearance: 'none',
                      minHeight: '44px',
                      padding: '12px 16px 12px 40px'
                    }}
                  />
                </div>
                {searchQuery.trim() && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSearchQuery("")}
                    className="text-[#666666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5] touch-manipulation"
                    style={{ 
                      minHeight: '44px',
                      minWidth: '44px',
                      fontSize: '16px',
                      textAlign: 'center'
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-auto max-h-[calc(100vh-200px)] text-[#1a1a1a]">
              {parsedData && tableColumns.length > 0 ? (
                <div className="space-y-4">
                  <div className="text-sm font-semibold mb-2">STRUCTURED DATA:</div>
                  {filteredParsedData.length === 0 && searchQuery.trim() ? (
                    <div className="text-center py-8 text-[#9b9b9b]">
                      No results found for "{searchQuery}"
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-[#e0e0e0]">
                            {tableColumns.map((column) => (
                              <TableHead 
                                key={column} 
                                className="text-[#1a1a1a] border-[#e0e0e0]"
                              >
                                {column}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredParsedData.map((item, index) => {
                            // Use a unique key: prefer id or chunk_id, fallback to index with stable identifier
                            const uniqueKey = item.id || item.chunk_id || `${index}-${JSON.stringify(item).substring(0, 50)}`;
                            return (
                            <TableRow 
                              key={uniqueKey} 
                              className="border-[#e0e0e0] hover:bg-[#f5f5f5]"
                            >
                              {tableColumns.map((column) => {
                                const value = item[column];
                                const displayValue = typeof value === 'object' 
                                  ? JSON.stringify(value) 
                                  : String(value || '');
                                return (
                                  <TableCell 
                                    key={column}
                                    className="text-[#1a1a1a] border-[#e0e0e0]"
                                  >
                                    <div className="max-w-xs truncate" title={displayValue}>
                                      {searchQuery.trim() 
                                        ? highlightText(displayValue, searchQuery)
                                        : displayValue
                                      }
                                    </div>
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {searchQuery.trim() && filteredContent === '' ? (
                    <div className="text-center py-8 text-[#9b9b9b]">
                      No results found for "{searchQuery}"
                    </div>
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm font-mono">
                      {searchQuery.trim() 
                        ? highlightText(filteredContent, searchQuery)
                        : document.content
                      }
                    </pre>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

